<template>
  <div class="modal-content">
    <div class="modal-header">
      <h4 class="modal-title">{{ _('Create payment plan') }}</h4>
    </div>
    <div class="modal-body">
      <div class="form-group">
        <label>{{ _('Payment plan') }}</label>
        <p v-if="loadingPaymentPlans" class="form-control-static text-muted" style="margin-bottom: 0">
          <i class="fa fa-fw fa-spinner fa-spin"></i> {{ _('Loading...') }}
        </p>
        <select v-else class="form-control" v-model="selectedPlanId">
          <option value="">{{ _('Select') }}</option>
          <option v-for="plan in paymentPlans" :key="plan.id" :value="plan.id">{{ plan.name }}</option>
        </select>
      </div>

      <div class="form-group" style="margin-top: 20px">
        <div class="clearfix" style="margin-bottom: 8px">
          <label style="margin-bottom: 0">{{ _('Items to include in payment plan') }}</label>
          <div class="pull-right" style="font-size: 12px; line-height: 28px">
            <a href="javascript:;" @click="selectAll">{{ _('Select all') }}</a>
            &nbsp;/&nbsp;
            <a href="javascript:;" @click="deselectAll">{{ _('Deselect all') }}</a>
          </div>
        </div>

        <div v-if="!topLevelItems.length">
          {{ _('No items in order') }}
        </div>

        <table v-else class="table table-bordered table-hover" style="margin-bottom: 4px">
          <thead>
            <tr>
              <th style="width: 40px"></th>
              <th>{{ _('Product') }}</th>
              <th style="width: 80px; text-align: right">{{ _('Qty') }}</th>
              <th style="width: 130px; text-align: right">{{ _('Total') }}</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="(item, itemIndex) in topLevelItems" :key="itemIndex + '-' + item.id">
              <tr
                :class="{ 'success': isSelected(item.id) }"
                style="cursor: pointer"
                @click="toggleItem(item.id)"
              >
                <td style="text-align: center; vertical-align: middle">
                  <input type="checkbox" :checked="isSelected(item.id)" @click.stop @change="toggleItem(item.id)" />
                </td>
                <td style="vertical-align: middle">{{ item.product_name }}</td>
                <td style="text-align: right; vertical-align: middle">{{ item.count }}</td>
                <td style="text-align: right; vertical-align: middle">{{ $filters.currency(item.count * item.price, currency) }}</td>
              </tr>
              <tr
                v-for="child in childrenOf(item)"
                :key="'child-' + child.id"
              >
                <td style="border-top: none"></td>
                <td style="vertical-align: middle; padding-left: 28px; border-top: none">
                  <i class="fa fa-fw fa-level-up fa-rotate-90" style="opacity: 0.4; margin-right: 4px"></i>{{ child.product_name }}
                </td>
                <td style="text-align: right; vertical-align: middle; border-top: none">{{ child.count }}</td>
                <td style="text-align: right; vertical-align: middle; border-top: none">{{ $filters.currency(child.count * child.price, currency) }}</td>
              </tr>
            </template>
          </tbody>
        </table>

        <div v-if="topLevelItems.length" class="text-muted" style="font-size: 12px">
          {{ selectedItemIds.length }} / {{ topLevelItems.length }} {{ _('items selected') }}
        </div>
      </div>

      <template v-if="selectedPlanId && selectedItemIds.length">
        <div v-if="preview && preview.valid" class="alert alert-success" style="margin-top: 4px; margin-bottom: 0">
          <strong><i class="fa fa-fw fa-check-circle"></i> {{ _('Payment plan preview') }}</strong>
          <table class="table" style="margin-top: 8px; margin-bottom: 0; background: transparent">
            <tbody>
              <tr>
                <td style="border-top: none; padding: 2px 0">{{ _('Advance payment') }}</td>
                <td style="border-top: none; padding: 2px 0; text-align: right; font-weight: bold">{{ $filters.currency(preview.advanceAmount, currency) }}</td>
              </tr>
              <tr>
                <td style="padding: 2px 0">{{ _('Final payment') }}</td>
                <td style="padding: 2px 0; text-align: right; font-weight: bold">{{ $filters.currency(preview.finalAmount, currency) }}</td>
              </tr>
              <tr>
                <td style="padding: 2px 0">{{ _('Final payment due') }}</td>
                <td style="padding: 2px 0; text-align: right; font-weight: bold">{{ preview.finalPaymentDateFormatted }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-else-if="preview && preview.error === 'date_in_past'" class="alert alert-danger" style="margin-top: 4px; margin-bottom: 0">
          <i class="fa fa-fw fa-exclamation-circle"></i>
          {{ _('The final payment date') }} ({{ preview.finalPaymentDateFormatted }}) {{ _('is in the past. Select a different payment plan or items with a later start date.') }}
        </div>

        <div v-else-if="preview && preview.error === 'minimum_exceeds_total'" class="alert alert-warning" style="margin-top: 4px; margin-bottom: 0">
          <i class="fa fa-fw fa-exclamation-triangle"></i>
          {{ _('The total of the selected items') }} ({{ $filters.currency(preview.totalAmount, currency) }}) {{ _('is below the minimum advance payment required by this payment plan') }} ({{ $filters.currency(preview.minimumAmount, currency) }}). {{ _('Select more items or use a different payment plan.') }}
        </div>

        <div v-else-if="preview && preview.error === 'fixed_covers_full'" class="alert alert-warning" style="margin-top: 4px; margin-bottom: 0">
          <i class="fa fa-fw fa-exclamation-triangle"></i>
          {{ _('The fixed advance payment amount covers the full total of the selected items. A payment plan cannot be created.') }}
        </div>

        <div v-else-if="preview && preview.error === 'no_due_date'" class="alert alert-warning" style="margin-top: 4px; margin-bottom: 0">
          <i class="fa fa-fw fa-exclamation-triangle"></i>
          {{ _('The final payment date cannot be determined. This payment plan requires items with a start date.') }}
        </div>
      </template>
    </div>
    <div class="modal-footer">
      <div class="flex-row space-between">
        <button class="btn btn-info" @click="managePaymentplansDialog"><i class="fa fa-fw fa-gear"></i><span class="hidden-xs">{{ _('Manage payment plans') }}</span></button>
        <div class="flex-row button-col">
          <button class="btn btn-default" @click="$emit('close')">
            <i class="fa fa-fw fa-ban"></i> {{ _('Cancel') }}
          </button>
          <button class="btn btn-success" @click="confirm" :disabled="!canConfirm || loadingPaymentPlans">
            <i class="fa fa-fw fa-check"></i> {{ _('Select') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { DateTime } from 'luxon'
import errors from '~/lib/errors'
import modals from '~/lib/modals'
import PaymentPlansList from '~/components/modals/ModalPaymentPlansList'

export default {
  mixins: [errors, modals],
  props: ['baseDomain', 'shopId', 'apiKey', 'orderItems', 'currency', 'dateFormat'],
  data () {
    return {
      paymentPlans: [],
      loadingPaymentPlans: true,
      selectedPlanId: '',
      selectedItemIds: []
    }
  },
  mounted () {
    this.loadPaymentPlans()
  },
  computed: {
    topLevelItems () {
      return (this.orderItems || []).filter(i => !i.item_id)
    },
    preview () {
      if (!this.selectedPlanId || !this.selectedItemIds.length) {
        return null
      }
      const plan = this.paymentPlans.find(p => p.id === this.selectedPlanId)
      if (!plan) return null

      // Collect selected top-level items and their children (mirrors PHP ownItem iteration)
      const selectedItems = []
      for (const item of this.topLevelItems) {
        if (!this.isSelected(item.id)) continue
        selectedItems.push(item)
        selectedItems.push(...this.childrenOf(item))
      }

      // Compute full total and earliest start_ts across all selected items
      let fullTotal = 0
      let earliestTs = null
      for (const item of selectedItems) {
        const price = parseFloat(item.price) || 0
        const count = parseFloat(item.count) || 0
        const resourceCount = item.resource_count && parseInt(item.resource_count) > 1 ? parseInt(item.resource_count) : 1
        fullTotal += price * count * resourceCount
        const reservation = item.ownReservation && item.ownReservation[0] ? item.ownReservation[0] : null
        if (reservation) {
          const reservationStartTs = parseInt(reservation.start_ts)
          if (earliestTs === null || reservationStartTs < earliestTs) {
            earliestTs = reservationStartTs
          }
        } else if (item.start_ts) {
          const ts = parseInt(item.start_ts)
          if (earliestTs === null || ts < earliestTs) {
            earliestTs = ts
          }
        }
      }

      // Resolve the final payment due date timestamp
      let dueTs = null
      if (plan.final_payment_type === 'fixed' && plan.final_payment_date) {
        dueTs = Math.floor(new Date(plan.final_payment_date).getTime() / 1000)
      } else if (plan.final_payment_type === 'offset' && earliestTs !== null) {
        dueTs = earliestTs - (parseInt(plan.final_payment_offset) * 86400)
      }

      // Compute initial (advance) payment amount
      let initialAmount = 0
      let hasDeferredComponent = false

      if (plan.amount_type === 'relative') {
        const percentage = parseFloat(plan.amount) || 0
        const minimum = plan.minimum_amount ? parseFloat(plan.minimum_amount) : 0
        const computed = fullTotal * percentage / 100

        if (minimum > 0 && computed < minimum) {
          if (minimum >= fullTotal) {
            return {
              valid: false,
              error: 'minimum_exceeds_total',
              minimumAmount: Math.round(minimum * 100) / 100,
              totalAmount: Math.round(fullTotal * 100) / 100
            }
          }
          // Raise advance to the minimum amount (mirrors PHP behaviour)
          initialAmount = minimum
        } else {
          initialAmount = computed
        }
        hasDeferredComponent = true
      } else if (plan.amount_type === 'fixed') {
        const fixedAmount = parseFloat(plan.amount) || 0
        initialAmount = Math.min(fixedAmount, fullTotal)
        hasDeferredComponent = initialAmount < fullTotal

        if (!hasDeferredComponent) {
          return { valid: false, error: 'fixed_covers_full' }
        }
      }

      if (!hasDeferredComponent || dueTs === null) {
        return { valid: false, error: 'no_due_date' }
      }

      const nowTs = Math.floor(Date.now() / 1000)
      const dueDateFormatted = DateTime.fromSeconds(dueTs).toFormat(this.dateFormat || 'yyyy-MM-dd')

      if (dueTs <= nowTs) {
        return {
          valid: false,
          error: 'date_in_past',
          finalPaymentDateFormatted: dueDateFormatted
        }
      }

      const advanceAmount = Math.round(initialAmount * 100) / 100
      const totalAmount = Math.round(fullTotal * 100) / 100

      return {
        valid: true,
        advanceAmount,
        finalAmount: Math.round((totalAmount - advanceAmount) * 100) / 100,
        totalAmount,
        finalPaymentDateFormatted: dueDateFormatted
      }
    },
    canConfirm () {
      return !!(this.selectedPlanId && this.selectedItemIds.length && this.preview && this.preview.valid)
    }
  },
  methods: {
    managePaymentplansDialog () {
      this.showModal('paymentplanslist', PaymentPlansList, {
        props: {
          baseDomain: this.baseDomain,
          shopId: this.shopId,
          apiKey: this.apiKey,
          component: {
            initData: {
              modal: true
            }
          }
        },
        events: {},
        handlers: {
          closed: () => {
            this.loadPaymentPlans()
          }
        },
        width: 1000
      })
    },
    async loadPaymentPlans () {
      this.loadingPaymentPlans = true
      try {
        const response = await axios.get(this.baseDomain + this.shopId + '/paymentplans.json?apikey=' + this.apiKey)
        this.paymentPlans = response.data || []
      } catch (e) {
        this.showError(e)
        this.$emit('close')
        return
      } finally {
        this.loadingPaymentPlans = false
      }
    },
    childrenOf (item) {
      return (this.orderItems || []).filter(i => i.item_id === item.id)
    },
    isSelected (id) {
      return this.selectedItemIds.includes(id)
    },
    toggleItem (id) {
      const idx = this.selectedItemIds.indexOf(id)
      if (idx === -1) {
        this.selectedItemIds.push(id)
      } else {
        this.selectedItemIds.splice(idx, 1)
      }
    },
    selectAll () {
      this.selectedItemIds = this.topLevelItems.map(i => i.id)
    },
    deselectAll () {
      this.selectedItemIds = []
    },
    confirm () {
      this.$emit('confirm', {
        paymentPlanId: this.selectedPlanId,
        itemIds: [...this.selectedItemIds]
      })
    }
  }
}
</script>
